import React, { useState, useEffect } from 'react';
import {
    Container, Grid, Typography, Button, Box, Chip, Divider, LinearProgress
} from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../Auth';
import api from '../api/axiosInstance';

export default function Dashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    // eslint-disable-next-line
    const [ceremonySummary, setCeremonySummary] = useState({ ceremonies: [], next_upcoming: null });
    const ceremonies = ceremonySummary.ceremonies || [];
    // eslint-disable-next-line
    const [staleRecords, setStaleRecords] = useState([]);
    const [wwccStats, setWwccStats] = useState({
        expired: 0,
        soon: 0,
        verified: 0,
        invalid: 0,
        banned: 0,
        pending: 0
    });
    const [currentCount, setCurrentCount] = useState(0);
    const [limit, setLimit] = useState(0);

    const fetchDashboardData = async () => {
        if (!user) return;

        try {
            const requests = [];

            // 1. Fetch Ceremonies if enabled
            if (user?.enabled_modules?.includes('awards')) {
                requests.push(api.get('/ceremonies/dashboard-summary'));
            }
            // 2. Fetch WWCC Stats if enabled
            if (user?.enabled_modules?.includes('wwcc')) {
                requests.push(api.get('/wwcc/summary'));
                requests.push(api.get('/wwcc'));
            }

            const results = await Promise.all(requests);

            results.forEach((res) => {
                const url = res.config.url;

                if (url.includes('/ceremonies/dashboard-summary')) {
                    setCeremonySummary({
                        ceremonies: res.data?.ceremonies || [],
                        next_upcoming: res.data?.next_upcoming || null
                    });
                } else if (url.includes('/wwcc/summary')) {
                    const data = res.data;
                    setWwccStats({
                        expired: data.expired_count || 0,
                        soon: data.soon_count || 0,
                        verified: data.verified_count || 0,
                        invalid: data.invalid_count || 0,
                        banned: data.banned_count || 0,
                        pending: data.pending_count || 0
                    });
                } else if (url.includes('/wwcc')) {
                    // Filter out rows that haven't been validated or checked within 2 weeks
                    const allRows = res.data || [];
                    const twoWeeksAgo = new Date();
                    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);

                    const stale = allRows.filter(record => {
                        if (!record.last_checked_at) return true; // Never checked equals stale
                        return new Date(record.last_checked_at) < twoWeeksAgo;
                    });
                    setStaleRecords(stale);
                }
            });
        } catch (err) {
            console.error("Failed to fetch dashboard data:", err);
        }
    };

    useEffect(() => {
        fetchDashboardData();
        checkLimit();
        // eslint-disable-next-line
    }, [user?.tenant_id]);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Intl.DateTimeFormat('en-GB', {
            weekday: 'short', day: '2-digit', month: 'short', year: 'numeric'
        }).format(new Date(dateString));
    };

    const checkLimit = async () => {
        try {
            const response = await api.get('/wwcc/usage-status');
            setCurrentCount(response.data.currentCount);
            setLimit(response.data.limit);
        } catch (err) {
            console.error("Error checking subscription status", err);
            return false;
        }
    };

    const isAdmin = user?.role === 'admin' || user?.role === 'superuser';

    // Upcoming ceremonies only appear as cards when nothing is open/closed/finalised
    const activeCeremonies = ceremonies.filter(c => c.phase !== 'upcoming');
    const nextUpcoming = ceremonySummary.next_upcoming;

    const PHASES = {
        upcoming: { label: 'Upcoming', color: 'default' },
        open: { label: 'Open', color: 'success' },
        closed: { label: 'Closed', color: 'warning' },
        finalised: { label: 'Finalised', color: 'primary' }
    };

    const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

    const countdownText = (c) => {
        if (c.phase === 'upcoming') {
            return c.days_until_open === 1 ? 'Nominations open tomorrow' : `Nominations open in ${plural(c.days_until_open, 'day')}`;
        }
        if (c.phase === 'open') {
            if (c.days_until_cutoff === 0) return 'Nominations close today';
            return `Nominations close in ${plural(c.days_until_cutoff, 'day')}`;
        }
        const ago = -c.days_until_cutoff;
        return `Closed ${plural(ago, 'day')} ago`;
    };

    const ceremonyAction = (c) => {
        if (c.phase === 'open') {
            return { label: 'Nominate', onClick: () => navigate(`/nominations/${c.id}`, { state: { ceremonyName: c.name } }) };
        }
        if (c.phase === 'closed' && isAdmin) {
            return { label: 'Review nominations', onClick: () => navigate(`/ceremonies/${c.id}`) };
        }
        if (c.phase === 'finalised') {
            return { label: 'View winners', onClick: () => navigate(`/ceremonies/${c.id}`) };
        }
        return null;
    };

    const cardSx = {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        bgcolor: 'white',
        p: 4,
        borderRadius: 3,
        width: '100%',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.04)',
        border: '1px solid #f8fafc'
    };

    const usagePercentage = Math.min(Math.round((currentCount / limit) * 100), 100);

    const statusDataItems = [
        { label: 'Verified', count: wwccStats.verified, color: '#0f766e' },
        { label: 'Expiring soon', count: wwccStats.soon, color: '#d97706' },
        { label: 'Expired', count: wwccStats.expired, color: '#be123c' },
        { label: 'Invalid', count: wwccStats.invalid, color: '#4338ca' },
        { label: 'Banned', count: wwccStats.banned, color: '#111827' },
        { label: 'Pending', count: wwccStats.pending, color: '#6b7280' }
    ];

    return (
        <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                {user?.tenant_name ? `${user.tenant_name} Dashboard` : 'Dashboard'}
            </Typography>

            {/* --- AWARDS SECTION --- */}
            {user?.enabled_modules?.includes('awards') && (
                <Box sx={{ mb: 6 }}>
                    <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                        Active Ceremonies
                    </Typography>

                    <Grid container spacing={3} sx={{ width: '100%', margin: 0, display: 'flex', flexWrap: 'wrap' }}>
                        {activeCeremonies.length > 0 ? (
                            activeCeremonies.map((c) => {
                                const phase = PHASES[c.phase] || PHASES.upcoming;
                                const pct = c.total_slots > 0 ? Math.round((c.winners_chosen / c.total_slots) * 100) : 0;
                                const action = ceremonyAction(c);
                                return (
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={c.id} sx={{ display: 'flex' }}>
                                        <Box sx={cardSx}>
                                            <Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                                    <Box>
                                                        <Typography variant="h6" role="link" tabIndex={0} title="Open ceremony details" onClick={() => navigate(`/ceremonies/${c.id}`)} onKeyDown={(e) => e.key === 'Enter' && navigate(`/ceremonies/${c.id}`)} sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.3, cursor: 'pointer', '&:hover': { textDecoration: 'underline', color: '#1F8A70' } }}>
                                                            {c.name}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                                                            {formatDate(c.ceremony_date)}
                                                        </Typography>
                                                    </Box>
                                                    <Chip label={phase.label} color={phase.color} size="small" />
                                                </Box>

                                                <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500, mt: 2 }}>
                                                    {countdownText(c)}
                                                </Typography>

                                                <Box sx={{ mt: 3 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
                                                            Winners
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                                            {c.winners_chosen} of {c.total_slots} winners chosen
                                                        </Typography>
                                                    </Box>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={pct}
                                                        sx={{
                                                            height: 14,
                                                            borderRadius: '7px',
                                                            bgcolor: '#f1f5f9',
                                                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                                                            '& .MuiLinearProgress-bar': {
                                                                borderRadius: '7px',
                                                                background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                                                                boxShadow: '0px 1px 3px rgba(16, 185, 129, 0.4)'
                                                            }
                                                        }}
                                                    />
                                                </Box>

                                                <Typography variant="body2" sx={{ color: '#64748b', mt: 2 }}>
                                                    {isAdmin
                                                        ? `${c.pending_nominations} pending nomination${c.pending_nominations === 1 ? '' : 's'} · ${c.awards_without_nominations} award${c.awards_without_nominations === 1 ? '' : 's'} with no nominations`
                                                        : `You've submitted ${c.my_nominations} nomination${c.my_nominations === 1 ? '' : 's'}`}
                                                </Typography>
                                            </Box>

                                            {action && (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    fullWidth
                                                    sx={{ mt: 3 }}
                                                    onClick={action.onClick}
                                                >
                                                    {action.label}
                                                </Button>
                                            )}
                                        </Box>
                                    </Grid>
                                );
                            })
                        ) : (
                            <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: 'flex' }}>
                                <Box sx={cardSx}>
                                    {nextUpcoming ? (
                                        <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                                Next ceremony
                                            </Typography>
                                            <Typography variant="h6" role="link" tabIndex={0} title="Open ceremony details" onClick={() => navigate(`/ceremonies/${nextUpcoming.id}`)} onKeyDown={(e) => e.key === 'Enter' && navigate(`/ceremonies/${nextUpcoming.id}`)} sx={{ fontWeight: 700, color: '#0f172a', mt: 1, cursor: 'pointer', '&:hover': { textDecoration: 'underline', color: '#1F8A70' } }}>
                                                {nextUpcoming.name}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#64748b' }}>
                                                {formatDate(nextUpcoming.ceremony_date)}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500, mt: 2 }}>
                                                {countdownText(nextUpcoming)} ({formatDate(nextUpcoming.open_date)})
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box>
                                            <Typography variant="body1" sx={{ color: '#475569' }}>
                                                No upcoming ceremonies.
                                            </Typography>
                                            {!isAdmin && (
                                                <Typography variant="body2" sx={{ color: '#64748b', mt: 1 }}>
                                                    You'll see ceremonies here once your school schedules one.
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                    {!nextUpcoming && isAdmin && (
                                        <Button variant="contained" size="small" sx={{ mt: 3 }} onClick={() => navigate('/ceremonies')}>
                                            Create ceremony
                                        </Button>
                                    )}
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            )}

            {user?.enabled_modules?.includes('awards') && user?.enabled_modules?.includes('wwcc') && <Divider sx={{ mb: 6 }} />}

            {/* --- WWCC SECTION --- */}
            {user?.enabled_modules?.includes('wwcc') && (
                <Box sx={{ width: '100%', mt: 4 }}>
                    <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                        Compliance Overview (WWCC)
                    </Typography>

                    {/* display: 'stretch' forces items in rows to match heights natively */}
                    <Grid container spacing={4} sx={{ alignItems: 'stretch' }}>

                        {/* Left Box: Compliance Card Container */}
                        <Grid size={{ xs: 12, md: 8 }} sx={{ display: 'flex' }}>
                            <Box
                                onClick={() => navigate('/wwcc')}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    bgcolor: 'white',
                                    px: 4, py: 4,
                                    borderRadius: 3,
                                    width: '100%',
                                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
                                    cursor: 'pointer',
                                    transition: 'box-shadow 0.2s',
                                    '&:hover': { boxShadow: '0px 6px 24px rgba(0, 0, 0, 0.12)' }
                                }}
                            >
                                {/* Chart Wrapper */}
                                <Box sx={{
                                    position: 'relative',
                                    width: '400px',
                                    height: '400px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    mb: 4
                                }}>
                                    <Box sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                                        <Typography variant="h4" sx={{ fontWeight: '300', fontSize: '5rem', color: '#1f1f1f' }}>
                                            {currentCount}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: -1.9, fontSize: '1.5rem' }}>
                                            Total
                                        </Typography>
                                    </Box>

                                    <PieChart
                                        series={[{
                                            data: statusDataItems
                                                .map((item, index) => ({ id: index, value: item.count, color: item.color, label: item.label }))
                                                .filter(item => item.value > 0),
                                            innerRadius: 110,
                                            outerRadius: 190,
                                            paddingAngle: 2,
                                            cornerRadius: 4,
                                            arcLabel: (item) => `${Math.round((item.value / currentCount) * 100)}%`,
                                            arcLabelMinAngle: 15,
                                        }]}
                                        hideLegend
                                        width={420}
                                        height={420}
                                        slotProps={{
                                            pieArc: {
                                                style: { filter: 'drop-shadow(5px 5px 1px rgba(0, 0, 0, 0.15))' }
                                            },
                                            pieArcLabel: {
                                                style: {
                                                    fill: 'white',
                                                    fontWeight: '300',
                                                    fontSize: '0.85rem',
                                                    textShadow: '0px 1px 2px rgba(0,0,0,0.5)'
                                                }
                                            }
                                        }}
                                        // Remove the '& .MuiPieArcLabel-root' target entirely from sx
                                        sx={{}}
                                    />
                                </Box>

                                {/* Modern Bottom Label Grid Map */}
                                <Grid container spacing={2} sx={{ justifyContent: 'center', maxWidth: '600px', width: '100%' }}>
                                    {statusDataItems.map((item, idx) => (
                                        <Grid size={{ xs: 6, sm: 4 }} key={idx}>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                p: 1,
                                                borderRadius: 1.5,
                                                bgcolor: item.count > 0 ? '#f8fafc' : 'transparent',
                                                border: item.count > 0 ? '1px solid #f1f5f9' : '1px dashed #e2e8f0',
                                                opacity: item.count > 0 ? 1 : 0.45
                                            }}>
                                                <Box sx={{ width: 12, height: 12, bgcolor: item.color, borderRadius: '3px', flexShrink: 0 }} />
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'baseline',
                                                    justifyContent: 'space-between',
                                                    width: '100%',
                                                    minWidth: 0,
                                                    gap: 1
                                                }}>
                                                    <Typography noWrap variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                                        {item.label}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1e293b', flexShrink: 0 }}>
                                                        {item.count}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        </Grid>

                        {/* Right Box: Premium Minimalist Plan Usage Panel */}
                        <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex' }}>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between', // Keeps elements pinned to top and bottom cleanly
                                bgcolor: 'white',
                                p: 4,
                                borderRadius: 3,
                                width: '100%',
                                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.04)', // Softer shadow for true minimalism
                                border: '1px solid #f8fafc'
                            }}>
                                {/* Top Context Group */}
                                <Box>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 700,
                                            color: '#64748b',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.1em',
                                            display: 'block',
                                            mb: 1
                                        }}
                                    >
                                        Account Capacity
                                    </Typography>

                                    {/* Minimalist Typographic Metric (E.g. "450 of 1,000 used") */}
                                    <Box sx={{ mt: 3, mb: 1 }}>
                                        <Typography variant="h2" sx={{
                                            fontWeight: 300,
                                            fontSize: '5.5rem',
                                            color: '#0f172a',
                                            letterSpacing: '-0.02em',
                                            display: 'inline'
                                        }}>
                                            {currentCount}
                                        </Typography>
                                        <Typography variant="h4" sx={{
                                            color: '#94a3b8',
                                            fontWeight: 300,
                                            fontSize: '2.5rem',
                                            display: 'inline',
                                            ml: 1
                                        }}>
                                            of {limit}
                                        </Typography>
                                    </Box>

                                    <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 400, mt: 0.5 }}>
                                        active WWCC records currently monitored.
                                    </Typography>
                                </Box>

                                {/* Bottom Progress Line Group */}
                                <Box sx={{ mt: 6 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
                                            Allocation
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: usagePercentage >= 90 ? '#ef4444' : '#0f172a' }}>
                                            {usagePercentage}% Consumed
                                        </Typography>
                                    </Box>

                                    {/* Modern Premium Horizontal Gradient Progress Track */}
                                    <Box sx={{
                                        width: '100%',
                                        height: '50px',
                                        bgcolor: '#f1f5f9',
                                        borderRadius: '7px',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                                        mb: 1
                                    }}>
                                        <Box sx={{
                                            width: `${usagePercentage}%`,
                                            height: '100%',
                                            // Dynamic luxury gradient changing from emerald green into deep teal
                                            background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                                            borderRadius: '7px',
                                            boxShadow: '0px 1px 3px rgba(16, 185, 129, 0.4)',
                                            transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
                                        }} />
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>

                        {/* --- NEW SECTION: STALE VERIFICATIONS LIST (FULL WIDTH) --- */}
                        {/* {staleRecords.length > 0 && (
                            <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                                <Card sx={{
                                    borderRadius: 3,
                                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.04)',
                                    border: '1px solid #f8fafc',
                                    p: 4
                                }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#0f172a' }}>
                                            Attention Required: Stale Verifications (&gt; 2 Weeks)
                                        </Typography>
                                        <Chip
                                            label={`${staleRecords.length} Stale`}
                                            color="warning"
                                            size="small"
                                            sx={{ fontWeight: 'bold' }}
                                        />
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        The following active screening records have not been cross-referenced or updated with government portal databases within the last 14 days.
                                    </Typography>
                                    <Divider sx={{ mb: 2.5 }} />

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: '400px', overflowY: 'auto', pr: 1 }}>
                                        {staleRecords.map((record) => {
                                            const matchColor = statusDataItems.find(i => i.label.toLowerCase() === record.status?.toLowerCase())?.color || '#64748b';
                                            return (
                                                <Box
                                                    key={record.id}
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        p: 2,
                                                        borderRadius: 2,
                                                        bgcolor: '#f8fafc',
                                                        border: '1px solid #f1f5f9',
                                                        transition: 'background-color 0.2s',
                                                        '&:hover': { bgcolor: '#f1f5f9' }
                                                    }}
                                                >
                                                    <Box>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                                                            {record.first_given_name} {record.surname}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                            State Context: <strong>{record.au_state || 'N/A'}</strong> &bull; Last Checked: {record.last_checked_at ? formatDate(record.last_checked_at) : 'Never'}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Chip
                                                            label={record.status || 'UNKNOWN'}
                                                            size="small"
                                                            sx={{
                                                                fontWeight: 'bold',
                                                                bgcolor: `${matchColor}15`,
                                                                color: matchColor,
                                                                border: `1px solid ${matchColor}30`
                                                            }}
                                                        />
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => navigate('/wwcc', { state: { openRecordId: record.id } })}
                                                        >
                                                            Manage
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Card>
                            </Grid>
                        )} */}


                    </Grid>
                </Box>
            )}
        </Container>
    );
}